import { chunk, isNil, some } from "lodash";
import ActivityDetails from "PFApp/activities/show/activity_details";
import ActivityMatches from "PFApp/activities/show/activity_matches";
import { useActivityPageContext } from "PFApp/activities/show/activity_page_context";
import ActivityRoles from "PFApp/activities/show/activity_roles";
import ActivityShortlist from "PFApp/activities/show/activity_shortlist";
import { ActivityVacancies } from "PFApp/activities/show/activity_vacancies";
import { mapWorkflowStatesToUI } from "PFApp/audit_planner/parts/helpers";
import LazyLocalExportTrigger from "PFApp/components/document_buttons/lazy_local_export_trigger";
import ViewInBookingModuleButton from "PFApp/components/view_in_booking_module_button";
import MultiPage from "PFComponents/page/multi_page";
import PageCard from "PFComponents/page/page_card";
import PageTabs from "PFComponents/page/page_tabs";
import { useActivityParticipants } from "PFCore/hooks/queries/activity/use_activity_participants";
import { useInfiniteActivityWatchers } from "PFCore/hooks/queries/activity/use_activity_watchers";
import { getExportProfiles } from "PFCore/services/export_profiles";
import React, { useState } from "react";
import { Redirect } from "react-router-dom";

import ActivityInterested from "./activity_interested";
import css from "./activity_page.module.scss";
import ActivityShowMenuButton from "./details/activity_details_menu_button";
import { ActivityFeedback } from "./feedback/activity_feedback";
import { Actions } from "./parts/actions";

const ActivityPage = () => {
  const {
    task,
    roles,
    isEngagement,
    isRole,
    isAudit,
    matches,
    matchesMeta,
    shortlists,
    shortlistsMeta,
    suspendedShortlistsMeta,
    canSeeBookingModule,
    isActivityUnbiased,
    comparisonToastWrapp,
    tabs,
    showTabs,
    tabActiveId,
    tabsIds,
    handleChangeTab,
    isUnbiasedEnabled,
    vacancies,
    checkActivityPending
  } = useActivityPageContext();

  const { data, isFetched } = useInfiniteActivityWatchers(task, undefined, undefined, undefined, {
    enabled: !isFetched,
    onSuccess: (res) => {
      if (isNil(interestsCount)) {
        setInterestsCount(res.pages[0].meta.total);
      }
    }
  });

  const [interestsCount, setInterestsCount] = useState(data?.pages?.[0].meta.total);
  const { data: participants } = useActivityParticipants(task.id);
  const pendingFeedbacks =
    participants?.entries?.filter((participant) =>
      some(participant.feedback, ({ status }) => status === "pending")
    ) || [];
  const feedbackCount = pendingFeedbacks?.length === 0 ? undefined : pendingFeedbacks?.length;

  if (isAudit && isRole) {
    return (
      <Redirect
        to={`/audit_planner/control_centre/${mapWorkflowStatesToUI(
          mapWorkflowStatesToUI(task.workflow_state) === "pending"
            ? ""
            : `${task.parent_activity_id}/${task.id}`
        )}`}
      />
    );
  }

  const tabPages = [];

  tabPages.push(<ActivityDetails key={"details"} />);

  if (tabsIds.indexOf("interested") >= 0) {
    tabPages.push(
      <ActivityInterested
        key="interests"
        handleInterestsCountChange={(newCount) => setInterestsCount(newCount)}
      />
    );
  }

  if (tabsIds.indexOf("matches") >= 0) {
    tabPages.push(
      <ActivityMatches
        key={"matches"}
        handleTitleClick={() => handleChangeTab(tabs[0])}
        checkActivityPending={checkActivityPending}
        isEngagement={isEngagement}
        isRole={isRole}
      />
    );
  }

  if (tabsIds.indexOf("shortlist") >= 0) {
    tabPages.push(<ActivityShortlist key={"shortlist"} />);
  }

  if (tabsIds.indexOf("roles") >= 0) {
    tabPages.push(<ActivityRoles key={"roles"} />);
  }

  if (tabsIds.indexOf("vacancies") >= 0) {
    tabPages.push(<ActivityVacancies key="vacancies" />);
  }

  if (tabsIds.indexOf("feedback") >= 0) {
    tabPages.push(<ActivityFeedback key="feedback" />);
  }

  const isPageCardExpendable = tabActiveId === "interested";
  const isPageCardVisible = tabActiveId !== "feedback";

  const shortListCount = (shortlistsMeta?.total || 0) + (suspendedShortlistsMeta?.total || 0);
  const showActionsBelowHeader = showTabs;

  return (
    <>
      <div className={css.headerWrapper}>
        <div className={css.headerElements}>
          {!showActionsBelowHeader && <Actions task={task} className={css.topActions} />}
          {showTabs && (
            <PageTabs
              handleClick={(tab) => handleChangeTab(tab.id)}
              items={tabs}
              active={tabActiveId}
              counters={{
                shortlist: Math.max(shortListCount, 0),
                matches: matchesMeta.total,
                roles: roles.meta.total,
                vacancies: vacancies.meta.total,
                interested: interestsCount,
                feedback: feedbackCount
              }}
            />
          )}
          <div className={css.actions}>
            {canSeeBookingModule && (
              <ViewInBookingModuleButton
                openAllocateManually={tabActiveId === "shortlist" && shortListCount > 0}
              />
            )}
            {tabActiveId === "matches" && (
              <LazyLocalExportTrigger
                quantity={matches?.length || 0}
                getProfilesCollection={() => {
                  const items = matches.map(({ profile }) => profile);

                  return getExportProfiles({
                    profile_ids: items.map(({ id }) => id),
                    activity_id: task.id
                  }).then((resp) => ({
                    entries: resp.map((profile) => ({
                      profile
                    })),
                    meta: { total: resp.length, page: 1 }
                  }));
                }}
              />
            )}
            {tabActiveId === "shortlist" && (
              <LazyLocalExportTrigger
                quantity={shortlists?.length || 0}
                getProfilesCollection={(paginationPage = 1, paginationSize = 50) => {
                  const items = shortlists.map(({ profile }) => profile);
                  const paginatedArray = chunk(items, paginationSize);
                  return getExportProfiles({
                    profile_ids: paginatedArray[paginationPage - 1].map(({ id }) => id),
                    activity_id: task.id
                  }).then((resp) => ({
                    entries: resp.map((profile) => ({
                      profile
                    })),
                    meta: { total: resp.length, page: paginationPage }
                  }));
                }}
                isBlindModeOn={isUnbiasedEnabled && isActivityUnbiased}
              />
            )}
            <ActivityShowMenuButton />
          </div>
        </div>
        {showActionsBelowHeader && <Actions task={task} />}
      </div>
      {isPageCardVisible ? (
        <PageCard classes={isPageCardExpendable ? { card: css.expendableCard } : {}}>
          <MultiPage items={tabs} active={tabActiveId}>
            {tabPages}
          </MultiPage>
        </PageCard>
      ) : (
        <div className={css.minimalisticPageContainer}>
          <div>
            <MultiPage items={tabs} active={tabActiveId}>
              {tabPages}
            </MultiPage>
          </div>
        </div>
      )}
      {comparisonToastWrapp}
    </>
  );
};

export default ActivityPage;
